import { showErrorMessage, showSuccessMessage } from './messages'

let didAutoLogout = false;

const TOKEN_KEYS = [
    '@token',
    '@client_token',
    '@passenger_token',
    '@driver_token',
    '@supplier_token',
    '@super_admin_token',
    '@employee_code'
];

const safeStringify = (value) => {
    try {
        return JSON.stringify(value)
    } catch (_) {
        return String(value)
    }
}

const extractErrorText = (payload) => {
    if (!payload) return ''
    if (typeof payload === 'string') return payload
    if (typeof payload.error === 'string') return payload.error
    if (typeof payload.message === 'string') return payload.message
    if (typeof payload.body?.error === 'string') return payload.body.error
    if (typeof payload.body?.message === 'string') return payload.body.message
    if (typeof payload.error?.message === 'string') return payload.error.message
    if (typeof payload.body?.error?.message === 'string') return payload.body.error.message
    return safeStringify(payload)
}

const normalizeApiError = (errorLike = {}, context = {}) => {
    const payload = errorLike?.message && typeof errorLike.message === 'object'
        ? errorLike.message
        : (errorLike?.body || errorLike?.response?.data || errorLike)
    const status = errorLike?.status || errorLike?.response?.status || context?.status || null
    const logId = payload?.log_id || payload?.meta?.log_id || errorLike?.response?.headers?.['x-log-id'] || context?.logId || null
    const isHtmlError = typeof payload === 'string' &&
        /<\s*(?:!doctype\s+html|html|head|body)\b/i.test(payload)
    const backendError = isHtmlError ? '' : extractErrorText(payload)
    const networkError = !status && !payload

    let message = isHtmlError
        ? 'No se pudo conectar con el Servidor. Contacte al administrador'
        : backendError || errorLike?.message || 'No se pudo conectar con el Servidor. Contacte al administrador'
    if (typeof message !== 'string') {
        message = extractErrorText(message)
    }
    if (logId && typeof message === 'string' && !message.includes('Log ID:')) {
        message = `${message} (Log ID: ${logId})`
    }

    return {
        message,
        status,
        statusError: !!status,
        backend_error: backendError || null,
        network_error: networkError,
        log_id: logId,
        body: payload,
    }
}

const pickAuthToken = () => {
    // [SMART TOKEN SELECTION]
    // Helper: sessionStorage is used for impersonated tabs (per-tab isolation),
    // localStorage is used for regular sessions.
    const getVal = (key) => sessionStorage.getItem(key) || localStorage.getItem(key);

    // Check if this specific tab has an active impersonation role
    const activeRole = sessionStorage.getItem('active_role');

    if (activeRole) {
        let keyToPrioritize = null;
        switch (activeRole) {
            case 'client': keyToPrioritize = '@client_token'; break;
            case 'driver': keyToPrioritize = '@driver_token'; break;
            case 'supplier': keyToPrioritize = '@supplier_token'; break;
            case 'passenger': keyToPrioritize = '@passenger_token'; break;
            case 'user': keyToPrioritize = '@token'; break;
            default: break;
        }

        if (keyToPrioritize) {
            const val = getVal(keyToPrioritize);
            // If the specific token for the role exists, use it!
            if (val) return { key: keyToPrioritize, value: val };
        }
    }

    // Fallback: Standard priority check (sessionStorage first, then localStorage)
    for (const k of TOKEN_KEYS) {
        const val = getVal(k);
        if (val) return { key: k, value: val };
    }
    return null;
}

export const callApi = async (url, options = {}) => {
    let {
        json = true,
        json_response = true,
        params = {},
        shouldBuildUrl = true,
        method = 'GET',
        headers = {},
        successMessage = undefined,
        errorMessage = undefined,
        shouldSkipStatusError = false,
        showDefaultAlertError = true,
        maxRetries = 0,        // Reducido de 3 a 1 para evitar sobrecarga con muchos usuarios
        retryDelay = 2000,     // Aumentado de 1000 a 2000ms para dar más tiempo al servidor
    } = options

    if (json) {
        headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...headers,
        }

        // Auto-stringify body if it's an object
        if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
            options.body = JSON.stringify(options.body)
        }
    } else {
        if (method !== 'GET' && options.body) {
            let formBody = new FormData()
            for (var key in options.body) {
                if (Array.isArray(options.body[key])) {
                    for (var subKey in options.body[key]) {
                        formBody.append(key, options.body[key][subKey])
                    }
                } else {
                    formBody.append(key, options.body[key])
                }
            }

            options.body = formBody
        }
    }

    // Auto-add Authorization header if present in localStorage
    const auth = pickAuthToken()
    if (auth && !headers.Authorization && !headers.authorization) {
        headers.Authorization = `Bearer ${auth.value}`
    }

    //  Build URL

    const fullURL = shouldBuildUrl
        ? new URL(`${process.env.REACT_APP_API_ENDPOINT}${url}`)
        : new URL(url)

    //  Add custom query parameters
    Object.keys(params).forEach((key) =>
        fullURL.searchParams.append(key, params[key])
    )

    // Función helper para esperar con backoff exponencial
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Lógica de reintentos
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetch(fullURL, {
                ...options,
                method,
                headers,
            })

            let body
            if (json_response) {
                const text = await response.text()
                try {
                    body = text ? JSON.parse(text) : null
                } catch (_) {
                    body = text
                }
            } else {
                body = response
            }

            // Manejar 429 (Too Many Requests) con reintento
            if (response.status === 429 && attempt < maxRetries) {
                const retryAfter = response.headers.get('Retry-After');
                // Agregar jitter aleatorio (0-1000ms) para evitar thundering herd
                const jitter = Math.random() * 1000;
                const waitTime = retryAfter
                    ? parseInt(retryAfter) * 1000
                    : (retryDelay * Math.pow(2, attempt)) + jitter;


                console.warn(`Rate limited (429). Reintentando en ${Math.round(waitTime)}ms... (intento ${attempt + 1}/${maxRetries})`);
                await sleep(waitTime);
                continue; // Reintentar
            }

            if (!shouldSkipStatusError) {
                if (!response.ok) {
                    if (errorMessage) {
                        throw normalizeApiError(
                            { message: errorMessage },
                            { status: response.status, logId: response.headers.get('X-Log-Id') }
                        )
                    }
                    throw normalizeApiError(
                        { body, status: response.status },
                        { status: response.status, logId: response.headers.get('X-Log-Id') }
                    )
                } else if (successMessage) {
                    showSuccessMessage(successMessage)
                }
            }

            return {
                status: response.status,
                body,
            }
        } catch (e) {
            const normalizedError = normalizeApiError(e)
            lastError = normalizedError;

            //  Return to login page if the request fail with 401
            if (normalizedError.status === 401) {

                if(e?.message != "Su sesión ha expirado"){
                    console.log("error callapi:", e);
                } else {
                    didAutoLogout = true;

                    const wasKicked = e?.body?.session_kicked === true || normalizedError?.body?.session_kicked === true;

                    localStorage.setItem("@flash", JSON.stringify({
                        type: 'error',
                        text: wasKicked
                            ? 'La sesión se desconectó por inicio de sesión en otro dispositivo. Por favor, inicie sesión nuevamente.'
                            : 'Tu sesión ha expirado.'
                    }));
                    TOKEN_KEYS.forEach(k => localStorage.removeItem(k));
                    window.location.replace('/');
                    localStorage.removeItem('@token')
                    localStorage.removeItem('@super_admin_token')
                    console.error("401 detectado. Redirección deshabilitada temporalmente.");
                    throw { ...normalizedError, message: "Unauthorized", status: 401 }
                }

            }

            // Reintentar en caso de error de red (si no es el último intento)
            if (attempt < maxRetries && (!normalizedError.status || normalizedError.status >= 500)) {
                // Agregar jitter aleatorio para distribuir reintentos
                const jitter = Math.random() * 1000;
                const waitTime = (retryDelay * Math.pow(2, attempt)) + jitter;
                console.warn(`Error de red. Reintentando en ${Math.round(waitTime)}ms... (intento ${attempt + 1}/${maxRetries})`);
                await sleep(waitTime);
                continue;
            }

            // Si es el último intento o un error que no debe reintentar, lanzar error
            if (showDefaultAlertError) {
                showErrorMessage({ error: normalizedError.message })
            }

            throw normalizedError
        }
    }

    // Si llegamos aquí, todos los reintentos fallaron
    if (lastError) {
        throw lastError;
    }
}

export const fetchRaw = async (url, options) => {
    const res = await fetch(url, options)
    if (res.status !== 200) {
        const body = await res.json()
        return Promise.reject(body)
    } else {
        return res.blob()
    }
}
